import React, { useCallback, useContext, useEffect, useState } from 'react';

import NozomiEventConfigContext from '@totem/components/settings/sections/nozomi/eventConfiguration/nozomiEventConfigContext';
import {
  NozimiEventConfigurationData,
  NozomiEventConfiguration,
  RiskItem,
  TypeItem,
} from '@totem/components/settings/sections/nozomi/eventConfiguration/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNull } from '@totem/utilities/common';
import { NOZOMI_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  children?: React.ReactNode;
};
const NozomiEventConfigContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [actionExtras, setActionExtras] = useState<string>('');
  const [refresh, setRefresh] = useState<boolean>(true);
  const [data, setData] = useState<NozomiEventConfiguration>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isSending, setIsSending] = useState(false);
  useEffect(() => {
    setRefresh(true);
  }, [userProfile.organization.id]);

  /* Fetch Data */
  useEffect(() => {
    if (!refresh) {
      return;
    }
    setRefresh(false);

    setIsLoading(true);

    fetch(`${NOZOMI_ENDPOINT}/eventConfiguration`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: NozomiEventConfiguration) => {
        if (isNull(result.organization.typeConfiguration)) {
          result.organization.typeConfiguration = [];
        }
        if (isNull(result.organization.riskConfiguration)) {
          result.organization.riskConfiguration = [];
        }
        setData(result);
      })
      .then(() => {
        setIsLoading(false);
      });
  }, [refresh, getApiToken]);
  const sendNozomiConfig = useCallback(
    async (payload: NozimiEventConfigurationData) => {
      if (!isSending) {
        fetch(`${NOZOMI_ENDPOINT}/eventConfiguration`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            setHasChanges(false);

            setRefresh(true);
          }
        });
      }
    }, // End async
    [],
  );
  const updateData = (newData: NozomiEventConfiguration) => {
    setHasChanges(true);

    setData(newData);
  };
  const updateRisk = (old: RiskItem, newValue: RiskItem) => {
    setHasChanges(true);

    setData((prevState) => {
      return {
        ...prevState,
        organization: {
          ...prevState.organization,
          riskConfiguration: prevState.organization.riskConfiguration.map(
            (item) => {
              if (item.risk === old.risk) {
                return {
                  ...item,
                  risk: newValue.risk,
                  import: newValue.import,
                  eventSeverity: newValue.eventSeverity,
                };
              }
              return item;
            },
          ),
        },
      };
    });
  };
  const updateType = (old: TypeItem, newValue: TypeItem) => {
    setHasChanges(true);

    setData((prevState) => {
      return {
        ...prevState,
        organization: {
          ...prevState.organization,
          typeConfiguration: prevState.organization.typeConfiguration.map(
            (item) => {
              if (item.name === old.name) {
                return {
                  ...item,
                  name: newValue.name,
                  import: newValue.import,
                  eventSeverity: newValue.eventSeverity,
                };
              }
              return item;
            },
          ),
        },
      };
    });
  };
  const updateTypeByIndex = (index: number, newValue: TypeItem) => {
    setHasChanges(true);

    setData((prevState) => {
      return {
        ...prevState,
        organization: {
          ...prevState.organization,
          typeConfiguration: prevState.organization.typeConfiguration.map(
            (item, itemIndex) => {
              if (itemIndex === index) {
                return {
                  ...item,
                  name: newValue.name,
                  import: newValue.import,
                  eventSeverity: newValue.eventSeverity,
                };
              }
              return item;
            },
          ),
        },
      };
    });
  };
  const handleAction = (newAction: string, newActionExtras: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);
        break;
      case 'save':
        sendNozomiConfig(data.organization);
        break;
      default:
        setAction(newAction);

        setActionExtras(newActionExtras);
    }
  };
  return (
    <NozomiEventConfigContext.Provider
      value={{
        loading: isLoading,
        action,
        actionExtras,
        onAction: handleAction,
        data,
        setData: updateData,
        updateRisk,
        updateType,
        updateTypeByIndex,
        hasChanges,
      }}
    >
      {children}
    </NozomiEventConfigContext.Provider>
  );
};
export default NozomiEventConfigContainer;
